/**
 * One unanswered Google review, with an AI-drafted reply.
 *
 * The draft is a SUGGESTION; posting is a deliberate, confirmed act.
 *
 * This card used to end at copy-paste, on the reasoning that a reply appears
 * in public under the client's name and an unsupervised post cannot be quietly
 * taken back. Posting now happens here, so that caution moved rather than
 * disappeared: nothing leaves without a confirmation step that shows the exact
 * text, and the backend re-checks the words actually being sent — every rule
 * in the drafter lives in the PROMPT and stops applying the moment a person
 * edits the text.
 *
 * "Sent" means Google ACCEPTED the reply. Google moderates owner replies
 * before showing them, so the words are not public the instant this succeeds.
 */
import { useState } from "react";
import { AlertCircle, Check, Copy, Loader2, Send, Sparkles, Star } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	gbpAuditService,
	type GbpPendingReview,
	type GbpWithheldField,
} from "@/services/gbpAuditService";

const TONES = [
	{ value: "professional", label: "Professional" },
	{ value: "warm", label: "Warm" },
	{ value: "apologetic", label: "Apologetic" },
	{ value: "brief", label: "Brief" },
] as const;

type Tone = (typeof TONES)[number]["value"];

interface Props {
	tenantId: string;
	review: GbpPendingReview;
	placeId?: string;
	/** Called once Google has accepted a reply, so the list can refresh. */
	onPosted?: () => void;
}

/** Mirrors describeDecideFailure in GbpAuditPanel: same shape, same rules.
 *  Every branch must make clear the reply did NOT go out — a false success
 *  here is the worst outcome available, because the operator stops checking. */
const describePostFailure = (err: unknown): string => {
	const status = (err as { response?: { status?: number } })?.response?.status;
	if (status === 403)
		return "You do not have permission to post replies. Ask an operator to send it.";
	if (status === 401)
		return "Your session has expired. Reload the page and sign in again. Nothing was sent.";
	if (status === 503)
		return "Posting replies is turned off. Nothing was sent.";
	if (status === 422)
		return "The reply was rejected as malformed. This is a bug — please report it. Nothing was sent.";
	return "The reply could not be sent. Nothing was posted to Google.";
};

/** Why the DRAFT failed, in words the operator can act on.
 *
 * INCIDENT, 2026-09-23. Production refused this call with 403 — the signed-in
 * account was a gmail address, and the GBP surface is operator-only — and the
 * card said "Couldn't draft a reply just now. Write your own, or try again."
 *
 * Retrying was the one thing that could never work. A permission problem told
 * to try again reads as a flaky tool, and the operator keeps pressing instead
 * of signing in with the right account.
 *
 * Deliberately says nothing about sending: drafting reaches no client. That is
 * the difference from describePostFailure above, whose every branch has to
 * make clear the reply did NOT go out.
 */
const describeDraftFailure = (err: unknown): string => {
	const status = (err as { response?: { status?: number } })?.response?.status;
	if (status === 403)
		return "Your account can't use the drafting tool — it is for operators. Sign in with your agency account, or write the reply yourself.";
	if (status === 401)
		return "Your session has expired. Reload the page and sign in again.";
	if (status === 503)
		return "The drafting service is switched off. Write your own reply.";
	return "Couldn't draft a reply just now. Write your own, or try again.";
};

const formatDate = (value: string): string => {
	if (!value) return "";
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime())
		? value
		: parsed.toLocaleDateString(undefined, {
				year: "numeric",
				month: "short",
				day: "numeric",
			});
};

export default function ReviewReplyCard({
	tenantId,
	review,
	placeId,
	onPosted,
}: Props) {
	const [draft, setDraft] = useState("");
	const [tone, setTone] = useState<Tone>("professional");
	const [loading, setLoading] = useState(false);
	const [copied, setCopied] = useState(false);
	// Distinct from an empty draft: "we tried and got nothing" must not look
	// like "you have not asked yet".
	const [failed, setFailed] = useState<string | null>(null);
	// Anything the drafter refused to use because Google contradicts our
	// record. Without showing this, a draft with no phone number reads as
	// the tool forgetting rather than protecting the client.
	const [withheld, setWithheld] = useState<GbpWithheldField[]>([]);
	// Posting state. `confirming` gates the one irreversible-ish action on this
	// card behind a step that shows the exact words being published.
	const [confirming, setConfirming] = useState(false);
	const [posting, setPosting] = useState(false);
	const [postError, setPostError] = useState("");
	const [posted, setPosted] = useState(false);
	// Assertions the backend wants a person to vouch for before it will send.
	// Shown, never silently stripped — the operator may well know they are true.
	const [claims, setClaims] = useState<string[]>([]);
	// The editor stays once it has been opened. Gating it on `draft` meant that
	// selecting all and deleting — the obvious way to rewrite a reply from
	// scratch — unmounted the textarea and took the buttons with it.
	const [opened, setOpened] = useState(false);

	const rating = review.rating ?? 0;
	const negative = rating > 0 && rating <= 2;

	const send = async (acknowledgedClaims: boolean) => {
		setPosting(true);
		setPostError("");
		try {
			const result = await gbpAuditService.postReply({
				tenantId,
				reviewId: review.reviewId,
				reply: draft,
				placeId,
				acknowledgedClaims,
			});
			if (result.ok) {
				setPosted(true);
				setConfirming(false);
				setClaims([]);
				onPosted?.();
				return;
			}
			// A refusal, not a crash. Show the reason and — when the backend is
			// asking the operator to vouch for a claim — keep the dialog open so
			// they can confirm rather than retype everything.
			setClaims(result.claims);
			setPostError(result.reason || "The reply was not sent.");
		} catch (err) {
			setPostError(describePostFailure(err));
		} finally {
			setPosting(false);
		}
	};

	const generate = async () => {
		setLoading(true);
		setFailed(null);
		try {
			const draft = await gbpAuditService.suggestReply({
				tenantId,
				reviewId: review.reviewId,
				rating: review.rating,
				comment: review.comment,
				reviewer: review.reviewer,
				tone,
			});
			setDraft(draft.reply);
			setWithheld(draft.withheld);
			/* An empty draft is not an error the server reported — the
			   drafter withheld it. Keep the generic line for that. */
			setFailed(draft.reply ? null : describeDraftFailure(undefined));
			// Once there is something to edit, the editor stays put even if the
			// operator clears it to rewrite from scratch.
			if (draft.reply) setOpened(true);
		} catch (err) {
			// Never surface the raw error: it names the model provider. The
			// status is read, though — a 403 must not be told to try again.
			setDraft("");
			setWithheld([]);
			setFailed(describeDraftFailure(err));
		} finally {
			setLoading(false);
		}
	};

	const copy = async () => {
		try {
			await navigator.clipboard.writeText(draft);
			setCopied(true);
			window.setTimeout(() => setCopied(false), 2000);
		} catch {
			setCopied(false);
		}
	};

	return (
		<div
			className="rounded-lg border border-border p-4 space-y-3"
			data-testid="gbp-review-card"
		>
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<div className="flex items-center gap-2 flex-wrap">
						<span className="inline-flex items-center gap-0.5">
							{[1, 2, 3, 4, 5].map((n) => (
								<Star
									key={n}
									className={
										n <= rating
											? "h-3.5 w-3.5 fill-current text-[hsl(var(--status-warning))]"
											: "h-3.5 w-3.5 text-muted-foreground/40"
									}
								/>
							))}
						</span>
						{review.reviewer && (
							<span className="text-sm font-medium truncate">
								{review.reviewer}
							</span>
						)}
						{review.dateCreated && (
							<span className="text-xs text-muted-foreground">
								{formatDate(review.dateCreated)}
							</span>
						)}
					</div>
					{review.comment ? (
						<p className="mt-2 text-sm text-foreground/90 whitespace-pre-line">
							{review.comment}
						</p>
					) : (
						<p className="mt-2 text-sm italic text-muted-foreground">
							Rating only — the reviewer left no comment.
						</p>
					)}
				</div>
				{negative && (
					<Badge variant="destructive" className="shrink-0">
						Reply first
					</Badge>
				)}
			</div>

			<div className="flex items-center gap-2 flex-wrap">
				<select
					value={tone}
					onChange={(e) => setTone(e.target.value as Tone)}
					className="h-8 rounded-md border border-input bg-background px-2 text-xs"
					aria-label="Reply tone"
				>
					{TONES.map((t) => (
						<option key={t.value} value={t.value}>
							{t.label}
						</option>
					))}
				</select>
				<Button
					size="sm"
					variant="outline"
					onClick={generate}
					disabled={loading}
				>
					{loading ? (
						<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
					) : (
						<Sparkles className="h-3.5 w-3.5 mr-1.5" />
					)}
					{draft ? "Draft again" : "Draft a reply"}
				</Button>
				{opened && (
					<Button size="sm" variant="ghost" onClick={copy} disabled={!draft.trim()}>
						{copied ? (
							<Check className="h-3.5 w-3.5 mr-1.5" />
						) : (
							<Copy className="h-3.5 w-3.5 mr-1.5" />
						)}
						{copied ? "Copied" : "Copy"}
					</Button>
				)}
				{opened && !posted && (
					<Button
						size="sm"
						onClick={() => {
							setPostError("");
							setClaims([]);
							setConfirming(true);
						}}
						disabled={posting || !draft.trim()}
						data-testid="gbp-post-reply"
					>
						<Send className="h-3.5 w-3.5 mr-1.5" />
						Post reply
					</Button>
				)}
			</div>

			{withheld.length > 0 && (
				<div
					className="flex gap-2 rounded-md border border-coral/40 bg-coral-light p-2 text-xs text-coral-dark"
					data-testid="gbp-withheld-note"
				>
					<AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
					<div>
						{withheld.map((w) => (
							<p key={w.field}>
								The {w.label} was left out — our records say{" "}
								<strong>{w.ours}</strong>, Google says{" "}
								<strong>{w.theirs}</strong>. Fix the record and it will be
								offered again.
							</p>
						))}
					</div>
				</div>
			)}

			{opened && (
				<div className="space-y-1.5">
					<Textarea
						value={draft}
						onChange={(e) => setDraft(e.target.value)}
						rows={4}
						className="text-sm"
						aria-label="Suggested reply"
					/>
					<p className="text-xs text-muted-foreground">
						A suggestion — edit it freely. Nothing is sent to Google until
						you press Post reply and confirm.
					</p>
				</div>
			)}

			{failed && (
				<p className="text-xs text-muted-foreground" data-testid="gbp-draft-error">
					{failed}
				</p>
			)}

			{posted && (
				<p
					className="flex items-start gap-1.5 text-xs text-muted-foreground"
					data-testid="gbp-post-reply-sent"
				>
					<Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
					<span>
						Sent to Google. Google reviews owner replies before showing
						them, so it may be a few minutes — occasionally longer —
						before it appears publicly.
					</span>
				</p>
			)}

			{postError && !confirming && (
				<p
					role="alert"
					className="text-xs text-destructive"
					data-testid="gbp-post-reply-error"
				>
					{postError}
				</p>
			)}

			<Dialog open={confirming} onOpenChange={(open) => !open && setConfirming(false)}>
				<DialogContent data-testid="gbp-post-reply-confirm">
					<DialogHeader>
						<DialogTitle>Post this reply publicly?</DialogTitle>
						<DialogDescription>
							It appears on Google under the client&apos;s name, visible to
							anyone reading their reviews.
						</DialogDescription>
					</DialogHeader>

					{/* The exact words, read-only. Confirming something other than
					    what is about to be sent would make the step theatre. */}
					<p className="rounded-md border border-border bg-muted p-3 text-sm whitespace-pre-line">
						{draft}
					</p>

					{claims.length > 0 && (
						<div
							className="flex gap-2 rounded-md border border-coral/40 bg-coral-light p-2 text-xs text-coral-dark"
							data-testid="gbp-post-reply-claims"
						>
							<AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
							<div>
								<p>
									This states <strong>{claims.join(", ")}</strong> on the
									client&apos;s behalf.
								</p>
								<p className="mt-1">
									We cannot verify that. Only confirm if the client has
									told you it is true.
								</p>
							</div>
						</div>
					)}

					{postError && (
						<p role="alert" className="text-xs text-destructive">
							{postError}
						</p>
					)}

					<DialogFooter>
						<Button
							size="sm"
							variant="outline"
							onClick={() => setConfirming(false)}
							disabled={posting}
						>
							Cancel
						</Button>
						<Button
							size="sm"
							onClick={() => send(claims.length > 0)}
							disabled={posting}
							data-testid="gbp-post-reply-confirm-send"
						>
							{posting && (
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
							)}
							{claims.length > 0 ? "Confirm and post" : "Post reply"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
